/**
 * TodoBlock — live session todo list of the builtin agent
 * (plan agent-todo-context-panel §A.1; visual reference: PlanCard in
 * AgentDetailView, assembled from existing agent design tokens).
 *
 * Visibility (owner decision 2026-07-23): renders nothing only for an EMPTY
 * list. A fully finished plan STAYS visible so the user sees it is complete —
 * it just collapses by default (header + count) and expands on click, the same
 * chevron affordance the entity cards use (ExpandableEntityCard). An active
 * plan (any in_progress/pending row) is expanded by default.
 */
import { useState, type JSX } from "react";
import type { TodoItem } from "@magnis/client-core";
import { Icon } from "../components/ui";

function rowIcon(status: TodoItem["status"]): JSX.Element {
  switch (status) {
    case "completed":
      return <Icon name="circle-check" size={16} />;
    case "cancelled":
      return <Icon name="close" size={16} />;
    default:
      return <Icon name="circle" size={16} />;
  }
}

export function TodoBlock({ items }: { readonly items: readonly TodoItem[] }): JSX.Element | null {
  const finished =
    items.length > 0 &&
    items.every((i) => i.status === "completed" || i.status === "cancelled");

  // null = follow the auto default (collapsed once finished, expanded while
  // active); a click pins an explicit choice that survives later updates.
  const [override, setOverride] = useState<boolean | null>(null);

  if (items.length === 0) return null;

  const collapsed = override ?? finished;
  const completedCount = items.filter((i) => i.status === "completed").length;

  return (
    // @tested-by: tst_fe_todoblock_001
    // The header stays put and only the rows scroll. It used to be the other
    // way round — the whole block scrolled inside the Context panel, so "Todo
    // 6/10" slid off the top and there was no telling what you were looking at.
    <div className="mx-4 mb-2 flex min-h-0 flex-col rounded-xl border border-[var(--color-agent-plan-border)] bg-[var(--color-agent-plan-bg)] overflow-hidden">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={() => { setOverride(!collapsed); }}
        data-testid="todo-header"
        className="flex w-full shrink-0 items-center justify-between border-b border-[var(--color-agent-plan-header-border)] px-3.5 py-2 bg-transparent border-x-0 border-t-0 cursor-pointer hover:bg-[var(--color-agent-plan-header-border)]/30"
      >
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[var(--color-agent-center-text-heading)]">
          <Icon name="tasks" size={15} />
          <span>Todo</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs ${
              finished ? "text-green" : "text-[var(--color-agent-center-text-muted)]"
            }`}
          >
            {completedCount}/{items.length}
          </span>
          {/* Chevron on the right — same affordance as the entity cards. */}
          <Icon
            name={collapsed ? "chevron-right" : "chevron-down"}
            size={14}
            className="text-[var(--color-agent-center-text-muted)]"
          />
        </div>
      </button>
      {!collapsed && (
        <div data-testid="todo-rows" className="min-h-0 overflow-y-auto px-3.5 py-1">
          {items.map((item, i) => {
            const inProgress = item.status === "in_progress";
            const muted = item.status === "completed" || item.status === "cancelled";
            return (
              <div key={i} className="flex items-start gap-2.5 py-1.5">
                <span
                  className={`mt-0.5 ${
                    item.status === "completed"
                      ? "text-green"
                      : inProgress
                        ? "text-accent"
                        : "text-[var(--color-agent-center-text-muted)]"
                  }`}
                >
                  {rowIcon(item.status)}
                </span>
                <span
                  className={`text-[13px] leading-5 ${
                    inProgress
                      ? "text-accent"
                      : muted
                        ? "text-[var(--color-agent-text-muted)]"
                        : "text-[var(--color-agent-center-text)]"
                  }`}
                >
                  {inProgress ? (item.active_form ?? item.content) : item.content}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
